import ProfileSubscriptionService from "@services/profile-subscription-service";
import { initialiseComponents } from "../../core";
import Component from "../component.abstract";
import { notifyFromAlert } from "../helpers/notifs";

interface ProfileSubscriptionsProps {
    url: string
}

const FREQUENCY_DAILY_ID = 0;
const FREQUENCY_WEEKLY_ID = 1;
const SUGGESTION_FULLTIME_ID = 0;
const SUGGESTION_INTERIM_ID = 2;

const RADIO_TRACKING_DAILY = '{ "event": "generic", "EventName": "checkbox_click", "EventInfo": "frequency", "cta": "radio" }';

export default class ProfileSubscriptions extends Component {
  private data: any;
  private fulltimeModalInput: HTMLInputElement;
  private fulltimeCheckbox: HTMLInputElement;
  private fulltimeWeeklyCheckbox: HTMLInputElement;
  private interimModalInput: HTMLInputElement;
  private interimCheckbox: HTMLInputElement;
  private interimWeeklyCheckbox: HTMLInputElement;
  private successMessageTemplate: HTMLTemplateElement;
  
  constructor(private props: ProfileSubscriptionsProps, element: HTMLElement) {
    super(props, element);
  }

  mounted(): void {    
    this.fulltimeModalInput = document.querySelector("#unsubscription-modal-fulltime");
    this.interimModalInput = document.querySelector("#unsubscription-modal-interim");
    this.successMessageTemplate = document.querySelector("#successMessage");

    if (this.props.url) {
      this.call(this.props.url);
    }
  }
  
  async save(): Promise<boolean> {
    const response = await fetch(this.props.url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        abonnements: this.data.abonnements,
      }),
    })
    
    if (!response.ok) {
      this.displayError();
    }
    
    return response.ok;
  }
  
  call(url:string): void {
      const service = new ProfileSubscriptionService(url);
      service.get((response:any) => {
        this.data = response;
        this.addSubscriptions();
        // Add tracking on page load
        if (this.data.trackingsLoad && this.data.trackingsLoad.length) {
          if (
            !("IntersectionObserver" in window) ||
            !("IntersectionObserverEntry" in window) ||
            !("intersectionRatio" in window.IntersectionObserverEntry.prototype)
          ) {
            // If IntersectionObserver is not available, just send the load event
            window.dataLayer.push(this.data.trackingsLoad[0]);
          } else {
            // Simulate a page change on tab change
            const tracking = this.data.trackingsLoad[0];
            tracking.event = "VirtualPageview";

            const observer = new IntersectionObserver(function (entries) {
              // When visibility state change, check if the current element is visible
              if (entries[0].intersectionRatio > 0) {
                // If it's visible, send the load event
                window.dataLayer.push(tracking);
              }
            });

            // Start observing the current element
            observer.observe(this.element);
          }
        }
      })
      service.setErrorHandler(() => {
          this.displayError();
      });
  }
  
  addSubscriptions():void {
    const ul = this.element.querySelector("ul");
    for (let i = 0; i < this.data.abonnements.length; i++) {
      const subscription = this.data.abonnements[i];
      const li = document.createElement("li");
      
      let frequencyInputs = "";
      if (subscription.idFrequency === FREQUENCY_DAILY_ID || subscription.idFrequency === FREQUENCY_WEEKLY_ID) {
          const radioName = `frequency_${subscription.id}`
          const radioDailyId = `${radioName}_0`
          const radioWeeklyId = `${radioName}_1`
          frequencyInputs = `
            <fieldset>
              <div>
                <input 
                  type="radio" name="${radioName}" id="${radioDailyId}" class="radio" 
                  data-subscription-id="${subscription.id}" 
                  data-subscription-value="0"
                  data-analytics-values-param='{ "event": "generic", "EventName": "checkbox_click", "EventInfo": "frequency", "cta": "radio", "wording": "quotidien" }'
                  data-action="analytics#push"
                  ${subscription.idFrequency === 0 ? "checked" : ""} />
                <label for="${radioDailyId}">Quotidien</label>
              </div>
              <div>
                <input 
                  type="radio" name="${radioName}" id="${radioWeeklyId}" class="radio" 
                  data-subscription-id="${subscription.id}"
                  data-subscription-value="1"
                  data-analytics-values-param='{ "event": "generic", "EventName": "checkbox_click", "EventInfo": "frequency", "cta": "radio", "wording": "hebdomadaire" }'
                  data-action="analytics#push"
                  ${subscription.idFrequency === 1 ? "checked" : ""} />
                <label for="${radioWeeklyId}">Hebdomadaire</label>
              </div>
            </fieldset>
          `;
      }
      
      li.innerHTML = `
          <input type="checkbox" id="${subscription.id}" ${subscription.isActive ? "checked" : ""}> 
          <label for="${subscription.id}">${subscription.label}</label>
          ${frequencyInputs}
      `;
      
      let checkbox = <HTMLInputElement>li.querySelector("input[type=checkbox]");
      checkbox.addEventListener("change", this.handleSubscriptionChange.bind(this));
      
      if (frequencyInputs) {
        li.querySelectorAll("input[type=radio]").forEach(
          (input: HTMLInputElement) => input.addEventListener("change", () => {
            this.handleFrequencyChange(input);
          })
        );
      }

      // Traitement spécifique pour les modales des suggestions d'emploi et d'interim
      if (subscription.id === SUGGESTION_FULLTIME_ID) {
        this.fulltimeCheckbox = checkbox;
        this.fulltimeWeeklyCheckbox = <HTMLInputElement>li.querySelector(
          `input[type=radio][data-subscription-value="1"]`
        );
      } else if (subscription.id === SUGGESTION_INTERIM_ID) {
        this.interimCheckbox = checkbox;
        this.interimWeeklyCheckbox = <HTMLInputElement>li.querySelector(
          `input[type=radio][data-subscription-value="1"]`
        );
      }
      
      ul.appendChild(li);
    }

    this.initSubscriptionHandler(SUGGESTION_FULLTIME_ID, "#unsubscription-fulltime-update", this.fulltimeModalInput, this.fulltimeCheckbox, this.fulltimeWeeklyCheckbox);
    this.initSubscriptionHandler(SUGGESTION_INTERIM_ID, "#unsubscription-interim-update", this.interimModalInput, this.interimCheckbox, this.interimWeeklyCheckbox);
    initialiseComponents();
  }
  
  async handleSubscriptionChange(event: Event): Promise<void> {
    for (let s = 0; s < this.data.abonnements.length; s++) {
      const sub = this.data.abonnements[s];
      if ((<HTMLElement>event.target).id === "" + sub.id) {
        sub.isActive = (<HTMLInputElement>event.target).checked;
        
        const saved = await this.save();
        if (saved) {
          if (!sub.isActive &&
            (sub.id === SUGGESTION_FULLTIME_ID || sub.id === SUGGESTION_INTERIM_ID)) {
            // On envoie un event de tracking si l'utilisateur désactive une suggestion d'emploi ou d'interim
            window.dataLayer.push({
              "event": "generic",
              "EventName": "checkbox_click",
              "eventInfo": "unsubscribe",
              "cta": "checkbox",
              "wording": sub.label
            });
            
            if (sub.idFrequency === FREQUENCY_DAILY_ID) {
              // Si l'utilisateur désactive les suggestions d'offre emploi ou d'interim alors qu'il avait un abonnement quotidien,
              // on affiche une modale pour proposer un abonnement hebdomadaire à la place
              if (sub.id === SUGGESTION_FULLTIME_ID) {
                this.fulltimeModalInput.checked = true;
              } else if (sub.id === SUGGESTION_INTERIM_ID) {
                this.interimModalInput.checked = true;
              }
            } else {
              this.displaySuccessMessage();
            }
          } else {
            this.displaySuccessMessage();
          }
        }
        
        break;
      }
    }
  }

  async handleFrequencyChange(input: HTMLInputElement): Promise<void> {
    const subscription = this.data.abonnements.find((s: any) => {
      return s.id.toString() === input.dataset.subscriptionId;
    });
    if (!subscription) {
      return;
    }
    
    subscription.idFrequency = +input.dataset.subscriptionValue;
    const saved = await this.save();
    if (saved) {
      this.displaySuccessMessage();
    }
  }

  // Gère le clic sur le bouton "Passer à l'envoi hebdomadaire" dans les modales
  initSubscriptionHandler(
    subscriptionId: number, updateButtonId: string, modalInput: HTMLInputElement, subscriptionCheckbox: HTMLInputElement, frequencyRadio: HTMLInputElement
  ): void {
    let updateButton = document.querySelector(updateButtonId);
    if (!updateButton || !this.fulltimeCheckbox || !this.fulltimeWeeklyCheckbox) {
      return;
    }

    updateButton.addEventListener("click", async () => {
      const subscription = this.data.abonnements.find(
        (s: any) => s.id === subscriptionId
      );
      if (!subscription) {
        return;
      }

      subscription.isActive = true;
      subscription.idFrequency = FREQUENCY_WEEKLY_ID;
      subscriptionCheckbox.checked = true;
      frequencyRadio.checked = true;

      const saved = await this.save();
      if (saved) {
        this.displaySuccessMessage();
        modalInput.checked = false;
      }
    });
  }
  
  displaySuccessMessage(): void {
    if (!this.successMessageTemplate) {
      return;
    }
    // Envoie un événement pour afficher le toast message de succès
    this.successMessageTemplate.dispatchEvent(
      new CustomEvent("subscription:saved")
    );
  }
  
  displayError():void {
      notifyFromAlert({
          "Icon": 2,
          "Message": "Aïe, une erreur est survenue, rechargez la page et réessayez.",
          "AnalyticsTrackings": "",
          "sticky": true
        }, true);
  }
}
